// httpPorts — the concrete `AppPorts` over the running server's HTTP API.
//
// This is bilda INFRASTRUCTURE for the real-app entry (`httpMain.tsx`): the
// record `AppPortsProvider` hands the pure host. It composes `httpClient`
// (fetch + envelope parsing) with the pure adapters (`materialAdapter`,
// `conversationAdapter`) and returns `PortResult` — every failure already
// parsed into the shell's one `ExternalError` vocabulary, never `undefined`.
//
// This file is now just the ASSEMBLY POINT: each BC's impl lives in its own
// `./httpPorts/<bc>.js` slice (one per context-map BC), typed against its port
// slice; `httpPorts` is their disjoint-key spread — value-identical to the old
// flat literal (see .claude/analysis/ports-decomposition-plan-2026-08-26.md). The
// shared transport primitive (`command`) + the anchor→wire helper live in
// `./httpPorts/shared.js`.
//
// WORKSPACE-FLAT (flattened-object-model-design.md): every command and read
// used to be scoped by an ACTIVE PROJECT id, sent as the `x-project-id`
// header (the server lifted it into `targetProjectId`). This shell no longer
// tracks or sends that header at all — `GET /state` without it already
// answers with the WHOLE WORKSPACE's resources (`packages/server/src/routes/
// state.ts`: "Absent → the whole workspace"), and commands that still declare
// an optional `targetProjectId` field (until server/core retire Project in a
// later step) simply go untargeted, which the app layer's existing
// first-live-project fallback already covers. No server change was needed for
// this step.
//
// Conversation identity is always supplied by the caller. This adapter owns no
// active-conversation register: tabs select aggregates explicitly.
//
// Path B scope: command+query only. No SSE, no client event-fold. Reads are
// one-shot; after a turn the reply is taken from the `sendTurn` output rather
// than re-projected from a synced event stream.

import { annotationHttp } from "./httpPorts/annotation.js";
import { briefHttp } from "./httpPorts/brief.js";
import { clipboardHttp } from "./httpPorts/clipboard.js";
import { contextHttp } from "./httpPorts/context.js";
import { conversationHttp } from "./httpPorts/conversation.js";
import { feedbackHttp } from "./httpPorts/feedback.js";
import { generationHttp } from "./httpPorts/generation.js";
import { iamHttp } from "./httpPorts/iam.js";
import { journalHttp } from "./httpPorts/journal.js";
import { landingHttp } from "./httpPorts/landing.js";
import { learningHttp } from "./httpPorts/learning.js";
import { materialEditingHttp } from "./httpPorts/materialEditing.js";
import { packagingHttp } from "./httpPorts/packaging.js";
import { projectHttp } from "./httpPorts/project.js";
import { proposalHttp } from "./httpPorts/proposal.js";
import { qualityHttp } from "./httpPorts/quality.js";
import { resourceHttp } from "./httpPorts/resource.js";
import { snapshotHttp } from "./httpPorts/snapshot.js";
import { templateHttp } from "./httpPorts/template.js";
import { versioningHttp } from "./httpPorts/versioning.js";
import { workspaceHttp } from "./httpPorts/workspace.js";
import type { AppPorts } from "./ports.js";

export const httpPorts: AppPorts = {
    ...conversationHttp,
    ...contextHttp,
    ...iamHttp,
    ...learningHttp,
    ...workspaceHttp,
    ...resourceHttp,
    ...materialEditingHttp,
    ...proposalHttp,
    ...templateHttp,
    ...packagingHttp,
    ...qualityHttp,
    ...versioningHttp,
    ...annotationHttp,
    ...snapshotHttp,
    ...generationHttp,
    ...journalHttp,
    ...clipboardHttp,
    ...briefHttp,
    ...landingHttp,
    ...projectHttp,
    ...feedbackHttp,
};
